import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { AddCircleOutline, Delete, EditOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import Page from '../../components/molecules/page'
import { FetchFromBackoffice } from '../../utils/request'
import { toAgo } from '../../functions'
import { useDispatch } from 'react-redux'
import { deleteRecipe } from '../../state/thunks/machines'
import { AppDispatch } from '../../state/configureStore'

export type RecipeLabItem = {
    id: string,
    name: string,
    version: string,
    createdAt: string,
    updatedAt: string,
    numberOfSteps: number,
    stepIds: string[]
}

const RecipeSelectionPage = (): React.ReactElement => {
    const navigate = useNavigate()
    const [selectedRecipeId, setSelectedRecipeId] = useState<string>('')
    const [recipes, setRecipes] = useState<RecipeLabItem[]>([])
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [deletingRecipe, setDeletingRecipe] = useState<string | null>(null)

    const dispatch = useDispatch<AppDispatch>()

    const selectedRecipe = useMemo(
        () => recipes.find(recipe => recipe.id === selectedRecipeId) ?? null,
        [selectedRecipeId, recipes],
    )

    const getRecipes = async (): Promise<RecipeLabItem[]> => {
        setIsFetching(true)
        const recipesResult = await FetchFromBackoffice<RecipeLabItem[]>('recipes/recipe-lab')
        const transformedRecipes: RecipeLabItem[] = recipesResult.map(recipe => ({
            ...recipe,
            version: String(recipe.version) 
        })).filter(r => Number(r.version) >= 2)

        return transformedRecipes
    }

    useEffect(() => {
        getRecipes()
            .then(fetchedRecipes => {
                setRecipes(fetchedRecipes)
                setIsFetching(false)
            })
            .catch(error => {
                setIsFetching(false)
            })
    }, [])

    const handleCreateNew = () => {
        navigate('/recipe-lab/New')
    }

    const handleEditRecipe = () => {
        if (!selectedRecipe) return
        navigate(`/recipe-lab/${selectedRecipeId}`)
    }

    const handleDeleteRecipe = ({ recipeId, articleNumber } : { recipeId: string, articleNumber: string }) => {
        setDeletingRecipe(recipeId)
        dispatch(deleteRecipe({ articleNumber: articleNumber, recipeId: recipeId, fromVersion: '0.7.0' }))
            .unwrap()
            .then(_ => {
                setRecipes(prev => prev.filter(r => r.id !== recipeId))
            })
            .catch(_ => {
                setDeleteError('Error deleting recipe! Try again later.')
            })
            .finally(() => setDeletingRecipe(null))
    }

    return (
        <Page title='Select Recipe'>
        <Paper sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box>
                <Typography variant='h6'>Recipe Library</Typography>
                <Typography variant='body2' color='text.secondary'>
                Select an existing recipe to continue editing, or create a new one.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25 }}>
                <Button startIcon={<AddCircleOutline />} variant='outlined' onClick={handleCreateNew}>
                Create new recipe
                </Button>
                <Button
                startIcon={<EditOutlined />}
                variant='contained'
                disabled={!selectedRecipe}
                onClick={handleEditRecipe}
                >
                Continue editing
                </Button>
            </Box>
            </Box>

            <TableContainer sx={{ mt: 2.5, border: '1px solid #DFE3E5', borderRadius: '10px' }}>
            <Table size='small'>
                <TableHead>
                <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Steps</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Updated At</TableCell>
                    <TableCell></TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                      {isFetching ? (
                            <TableRow>
                            <TableCell colSpan={5} align="center">
                                <CircularProgress size={20} />
                            </TableCell>
                            </TableRow>
                        ) : recipes.length === 0 ? (
                            <TableRow>
                            <TableCell colSpan={5} align="center">No recipes found</TableCell>
                            </TableRow>
                        ) : (
                        recipes.map(recipe => {
                            const recipeId = recipe.id
                            const isSelected = selectedRecipeId === recipeId
                            const protectedId = '3154ccd0-4512-4e23-b767-893fefd46e50'
                            const canDelete = recipeId !== protectedId
                            const recipeName = recipe.name
                            return (
                                <TableRow
                                    hover
                                    key={recipeId}
                                    onClick={() => setSelectedRecipeId(recipeId)}
                                    selected={isSelected}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell sx={{ fontWeight: isSelected ? 600 : 400 }}>{recipe.name}</TableCell>
                                    <TableCell>{recipe.numberOfSteps}</TableCell>
                                    <TableCell>{toAgo(new Date(recipe.createdAt))}</TableCell>
                                    <TableCell>{toAgo(new Date(recipe.updatedAt))}</TableCell>
                                    <TableCell align='right'>
                                        <IconButton
                                            size='small'
                                            disabled={!canDelete || deletingRecipe === recipeId}
                                            onClick={(event) => {
                                            event.stopPropagation()
                                            if (!canDelete) return
                                            handleDeleteRecipe({ recipeId, articleNumber: recipeName })
                                            }}
                                            sx={{ visibility: canDelete ? 'visible' : 'hidden' }}
                                        >
                                            {deletingRecipe === recipeId ? (
                                            <CircularProgress size={20} />
                                            ) : (
                                            <Delete fontSize='small' />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )
                        }))}
                </TableBody>
            </Table>
            </TableContainer>
            {deleteError && (
                <Alert
                    severity="error"
                    action={
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                    >
                    </IconButton>
                    }
                    sx={{ mb: 2, mt: 2 }}
                >
                    {deleteError}
                </Alert>
            )}

            {selectedRecipe ? (
            <Box sx={{ mt: 2.25 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                Selected recipe steps
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedRecipe.stepIds.filter(stepId => stepId !== 'spray-setup' && stepId !== 'spray-finilize' && stepId !== 'fan-stop').map((step, index) => (
                    <Chip key={`${step}-${index}`} label={step} size='small' />
                ))}
                </Box>
            </Box>
            ) : null}
        </Paper>
        </Page>
    )
}

export default RecipeSelectionPage
